import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Input } from "@shadcn-in-nx/ui/input";
import { Loader2, Search } from "lucide-react";

export default function WorkflowSelector({ onSelect }) {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const debounceRef = useRef(null);
    const containerRef = useRef(null);

    const { call: fetchWorkflows, response: data, isLoading } = useBluelagoon({
        method: "getAllAdminWorkflows",
    });

    const doSearch = useCallback((q) => {
        const params = { limit: 10, offset: 0, includeArchived: true };
        if (q?.trim()) params.search = q.trim();
        fetchWorkflows(params);
    }, [fetchWorkflows]);

    useEffect(() => { doSearch(""); }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(search), 300);
        return () => clearTimeout(debounceRef.current);
    }, [search]);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const workflows = data?.workflows || [];

    return (
        <div ref={containerRef} className="relative">
            <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => setOpen(true)}
                    placeholder={t("admin.automationPlayground.searchWorkflows")}
                    className="pl-8"
                />
                {isLoading && <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-slate-400" />}
            </div>
            {open && workflows.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                    {workflows.map((wf) => (
                        <button
                            key={wf.id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0"
                            onClick={() => { onSelect(wf); setOpen(false); setSearch(wf.name || wf.id); }}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium truncate">{wf.name || "Unnamed"}</span>
                                <span className="text-xs text-slate-400 ml-2 shrink-0">{wf.workflowType || wf.type}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                                <span className="font-mono truncate">{wf.id}</span>
                                {wf.companyName && <span>· {wf.companyName}</span>}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
